/**
 * TrainingCategories — the "5 S's" landing page for athletes.
 *
 * Mirrors the Paladin reference Sachin sent (2026-02-16). Mobile-first card
 * stack — each S has its own color, lucide icon, and a status badge that
 * reads LIVE when ≥1 workout is published in that category and COMING SOON
 * otherwise. LIVE cards deep-link to /dashboard/workouts?category=strength
 * which uses the existing filter pipeline on AthleteWorkouts.
 *
 * Backend already supports category on workouts (workouts.py) and programs
 * (programs.py); the new /api/workouts/category-counts endpoint surfaces
 * which categories are populated so the LIVE/COMING-SOON state is data-
 * driven, not hard-coded.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    Loader2, Zap, Dumbbell, Flame, Wind, Sparkles, CalendarDays, Lock, ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Display config for the categories. Daily Workouts goes first so athletes
// land on the most actionable bucket (today's session) before browsing the
// program libraries underneath. Sachin spec 2026-02-27.
const CATEGORY_CARDS = [
    {
        value: "daily", label: "Daily Workouts",
        tagline: "Today's session · Updated weekly",
        blurb: "Fresh workouts your coach drops in regularly. Open whichever one's queued up today.",
        icon: CalendarDays, accent: "#06B6D4",
    },
    {
        value: "speed", label: "Speed",
        tagline: "Accelerate · Decelerate · Change Direction",
        blurb: "Sprint mechanics, plyometrics, reactive power. Move faster than the moment.",
        icon: Zap, accent: "#F59E0B",
    },
    {
        value: "strength", label: "Strength",
        tagline: "Build the engine that powers everything",
        blurb: "Progressive overload programs from Foundations to Advanced. The full library is open.",
        icon: Dumbbell, accent: "#3B82F6",
    },
    {
        value: "stamina", label: "Stamina",
        tagline: "Conditioning · Energy Systems · Work Capacity",
        blurb: "Aerobic base, lactate threshold, sport-specific endurance protocols.",
        icon: Flame, accent: "#EF4444",
    },
    {
        value: "suppleness", label: "Suppleness",
        tagline: "Mobility · Flexibility · Recovery",
        blurb: "Joint health, soft-tissue work, structured recovery sessions to keep you durable.",
        icon: Wind, accent: "#10B981",
    },
    {
        value: "skill", label: "Skill",
        tagline: "Sport Movement · Coordination · Mastery",
        blurb: "Sport-specific patterns, technical refinement, motor learning drills.",
        icon: Sparkles, accent: "#A855F7",
    },
];

export default function TrainingCategories() {
    const { user, loading, activeAthleteId } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Resolve the athlete context for workout access checks. Use the URL
    // param if provided (deep link from a specific kid), else fall back
    // to AuthContext's auto-resolved active athlete. Without this,
    // parents landing on /dashboard/workouts directly were sending NO
    // athlete_id, causing the backend to return rank -1 ("locked") even
    // when their kid had an active subscription. Sachin 2026-02-17.
    const athleteId = searchParams.get("athlete_id") || activeAthleteId || "";
    const [counts, setCounts] = useState(null);

    useEffect(() => {
        if (!loading && !user) navigate("/", { replace: true });
    }, [loading, user, navigate]);

    useEffect(() => {
        if (!user) return;
        // Programs-first flow: counts come from program library now, not
        // raw workouts. A category is "live" once any program is published
        // in it. Sachin spec 2026-02-27.
        axios.get(`${API}/programs-category-counts`, { withCredentials: true })
            .then((r) => setCounts(r.data || {}))
            .catch(() => setCounts({}));
    }, [user, athleteId]);

    if (loading || !user) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }

    return (
        <div data-testid="training-categories" className="min-h-screen bg-brand-obsidian text-white pt-20 pb-24 md:pb-12 px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
                <h1 className="font-display text-3xl md:text-5xl tracking-wider mb-1">Programs</h1>
                <p className="text-sm text-zinc-400 mb-6">
                    Pick a category. Each one is its own program library — start with what your sport needs most.
                </p>
                <div className="space-y-3 md:space-y-4">
                    {CATEGORY_CARDS.map((cat) => {
                        const n = counts?.[cat.value] ?? 0;
                        const live = n > 0;
                        return (
                            <CategoryCard
                                key={cat.value}
                                cat={cat}
                                live={live}
                                count={n}
                                athleteId={athleteId}
                                loading={counts === null}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function CategoryCard({ cat, live, count, athleteId, loading }) {
    const Icon = cat.icon;
    const params = new URLSearchParams();
    params.set("category", cat.value);
    if (athleteId) params.set("athlete_id", athleteId);
    // Programs-first flow: tapping a category now opens the PROGRAMS list
    // for that category, not the flat workout list. Sachin spec 2026-02-27:
    // "programs should be containers, then when they pick a program, it
    // only shows what workouts are in that program."
    const href = `/dashboard/programs?${params.toString()}`;

    const accentStyle = {
        // Per-card tinted border + icon backdrop. Using rgba via style attr so
        // Tailwind's purge doesn't strip dynamic class names.
        borderColor: live ? `${cat.accent}88` : `${cat.accent}33`,
        background: `linear-gradient(135deg, ${cat.accent}10 0%, transparent 60%)`,
    };
    const iconStyle = {
        background: `${cat.accent}1F`,
        borderColor: `${cat.accent}66`,
        color: cat.accent,
    };

    const Wrapper = live ? Link : "div";
    const wrapperProps = live ? { to: href } : {};

    return (
        <Wrapper
            {...wrapperProps}
            data-testid={`training-card-${cat.value}`}
            data-state={live ? "live" : "soon"}
            className={`group block border-2 rounded-2xl px-4 md:px-5 py-4 md:py-5 select-none ${
                live
                    ? "cursor-pointer transition-shadow duration-200 active:scale-[0.985] hover:shadow-2xl motion-safe:hover:scale-[1.01] motion-safe:transition-transform"
                    : "opacity-60 cursor-not-allowed"
            }`}
            style={{ ...accentStyle, ...(live ? { touchAction: "manipulation" } : {}) }}
        >
            <div className="flex items-start gap-3 md:gap-4">
                <div
                    className="w-14 h-14 md:w-16 md:h-16 rounded-xl border-2 flex items-center justify-center flex-shrink-0"
                    style={iconStyle}
                >
                    <Icon className="w-6 h-6 md:w-7 md:h-7" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-display text-2xl md:text-3xl tracking-wider" style={{ color: "#fff" }}>
                            {cat.label}
                        </h2>
                        {loading ? (
                            <span
                                data-testid={`training-badge-${cat.value}`}
                                className="text-[10px] tracking-[0.25em] uppercase border border-zinc-700 text-zinc-500 px-2 py-0.5 rounded-full"
                            >
                                · · ·
                            </span>
                        ) : live ? (
                            <span
                                data-testid={`training-badge-${cat.value}`}
                                className="text-[10px] tracking-[0.25em] uppercase border border-emerald-500/60 text-emerald-300 px-2 py-0.5 rounded-full bg-emerald-500/10"
                            >
                                Live · {count}
                            </span>
                        ) : (
                            <span
                                data-testid={`training-badge-${cat.value}`}
                                className="text-[10px] tracking-[0.25em] uppercase border border-zinc-700 text-zinc-500 px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                            >
                                <Lock className="w-2.5 h-2.5" /> Coming Soon
                            </span>
                        )}
                    </div>
                    <div className="text-[13px] md:text-sm font-semibold mt-0.5" style={{ color: cat.accent }}>
                        {cat.tagline}
                    </div>
                    <p className="text-xs md:text-sm text-zinc-400 mt-2 leading-relaxed">
                        {cat.blurb}
                    </p>
                </div>
                {live && (
                    <ArrowRight className="w-5 h-5 text-zinc-500 group-hover:text-white transition-colors flex-shrink-0 mt-1" />
                )}
            </div>
        </Wrapper>
    );
}
